/**
 * Google Analytics utilities with enhanced logging
 * Provides functions to track events, page views, and conversions
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * Check if Google Analytics is properly initialized
 */
export const isGAInitialized = (): boolean => {
  const initialized = typeof window !== "undefined" && 
                     typeof window.gtag === "function" && 
                     Array.isArray(window.dataLayer);
  
  if (!initialized) {
    console.warn("⚠️ GA Analytics: Not initialized - tracking disabled");
  }
  
  return initialized;
};

/**
 * Track a page view
 */
export const trackPageView = (url: string, title?: string): void => {
  if (!isGAInitialized()) return;

  console.log(`📄 GA Page View: ${url}${title ? ` (${title})` : ""}`);
  
  window.gtag("config", process.env.NEXT_PUBLIC_GTM_ID || "", {
    page_path: url,
    page_title: title,
  });
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  parameters: Record<string, any> = {}
): void => {
  if (!isGAInitialized()) return;

  console.log(`🎯 GA Event: ${eventName}`, parameters);
  
  window.gtag("event", eventName, {
    event_category: parameters.category || "engagement",
    event_label: parameters.label,
    value: parameters.value,
    ...parameters,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmission = (
  formName: string,
  success: boolean = true,
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("form_submit", {
    category: "form",
    label: formName,
    success: success,
    ...additionalData,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (
  buttonName: string,
  location: string = "unknown",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("button_click", {
    category: "engagement",
    label: buttonName,
    location: location,
    ...additionalData,
  });
};

/**
 * Track service page visits
 */
export const trackServiceView = (
  serviceName: string,
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("service_view", {
    category: "services",
    label: serviceName,
    ...additionalData,
  });
};

/**
 * Track contact attempts
 * @deprecated Use specific tracking functions instead: trackContactForm, trackContactWhatsApp
 */
export const trackContactAttempt = (
  method: "form" | "whatsapp" | "email" | "phone",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("contact_attempt", {
    category: "conversion",
    label: method,
    method: method,
    ...additionalData,
  });
};

// ============================================================================
// CONTACT METHOD TRACKING
// Specific events for each contact method to track conversion funnel
// ============================================================================

/**
 * Track when user submits the contact form
 * GA4 Key Conversion Event — successful submissions only.
 *
 * GA4 marks key events by event name, so a failed send must not use
 * contact_form_submit: it used to, and every failed send counted as a lead.
 * Failures go to contact_form_error (not a key event) so they stay visible.
 */
export const trackContactForm = (
  formData: {
    success: boolean;
    formName?: string;
    fieldsFilled?: number;
  },
  additionalData: Record<string, any> = {}
): void => {
  if (!formData.success) {
    trackEvent("contact_form_error", {
      category: "error",
      label: formData.formName || "contact_form",
      method: "form",
      success: false,
      fields_filled: formData.fieldsFilled,
      conversion: false,
      ...additionalData,
    });
    return;
  }
  trackEvent("contact_form_submit", {
    category: "conversion",
    label: formData.formName || "contact_form",
    method: "form",
    success: formData.success,
    fields_filled: formData.fieldsFilled,
    conversion: true, // Mark as key conversion
    ...additionalData,
  });
};

/**
 * Track when user clicks WhatsApp button
 * GA4 Key Conversion Event
 */
export const trackContactWhatsApp = (
  location: string = "unknown",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("contact_whatsapp_click", {
    category: "conversion",
    label: location,
    method: "whatsapp",
    contact_type: "instant_messaging",
    conversion: true, // Mark as key conversion
    ...additionalData,
  });
};

/**
 * Track email link clicks
 */
export const trackContactEmail = (
  location: string = "unknown",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("contact_email_click", {
    category: "conversion",
    label: location,
    method: "email",
    contact_type: "email",
    ...additionalData,
  });
};

/**
 * Track phone link clicks
 */
export const trackContactPhone = (
  location: string = "unknown",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("contact_phone_click", {
    category: "conversion",
    label: location,
    method: "phone",
    contact_type: "phone",
    ...additionalData,
  });
};

/**
 * Track blog post reads
 */
export const trackBlogRead = (
  postTitle: string,
  category: string = "uncategorized",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("blog_read", {
    category: "content",
    label: postTitle,
    blog_category: category,
    ...additionalData,
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (percentage: number): void => {
  if (percentage % 25 !== 0) return; // Only track 25%, 50%, 75%, 100%
  
  trackEvent("scroll_depth", {
    category: "engagement",
    label: `${percentage}%`,
    value: percentage,
  });
};

/**
 * Track file downloads
 */
export const trackDownload = (
  fileName: string,
  fileType: string = "unknown",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("file_download", {
    category: "engagement",
    label: fileName,
    file_type: fileType,
    ...additionalData,
  });
};

/**
 * Track external link clicks
 */
export const trackExternalLink = (
  url: string,
  linkText: string = "",
  additionalData: Record<string, any> = {}
): void => {
  trackEvent("external_link_click", {
    category: "engagement",
    label: linkText || url,
    external_url: url,
    ...additionalData,
  });
};

/**
 * Debug function to log current GA status
 */
export const debugGAStatus = (): void => {
  if (typeof window === "undefined") {
    console.log("🔍 GA Debug: Running on server side");
    return;
  }

  console.log("🔍 GA Debug Status:");
  console.log("- gtag function:", typeof window.gtag);
  console.log("- dataLayer exists:", Array.isArray(window.dataLayer));
  console.log("- dataLayer length:", window.dataLayer?.length || 0);
  console.log("- GA ID:", process.env.NEXT_PUBLIC_GTM_ID || "Not set");
  
  if (window.dataLayer?.length > 0) {
    console.log("- Recent dataLayer entries:", window.dataLayer.slice(-5));
  }
};

/**
 * Initialize scroll tracking
 */
export const initScrollTracking = (): void => {
  if (typeof window === "undefined" || !isGAInitialized()) return;

  let scrollDepths = [25, 50, 75, 100];
  let trackedDepths: number[] = [];

  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );

    scrollDepths.forEach(depth => {
      if (scrollPercent >= depth && !trackedDepths.includes(depth)) {
        trackedDepths.push(depth);
        trackScrollDepth(depth);
      }
    });
  };

  window.addEventListener("scroll", handleScroll, { passive: true });
  
  console.log("📊 GA: Scroll tracking initialized");
};
